import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import api from "../lib/api";
import { BookOpen, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLOR = {
  Guide: "bg-nexus-purple/10 text-nexus-purple border-nexus-purple/30",
  Playbook: "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30",
  Explainer: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Creator Guide": "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
};

function catCls(c) {
  return CATEGORY_COLOR[c] || "bg-white/5 text-zinc-300 border-white/10";
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function BlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog")
      .then((r) => setPosts(r.data))
      .catch(() => toast.error("Failed to load blog"))
      .finally(() => setLoading(false));
  }, []);

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen grain">
      <SEO
        title="Blog & Guides · Creator Marketing Playbooks"
        description="Long-form guides for brands and creators on Nexus. How to hire UGC creators, price your rate card, run TikTok campaigns, and use escrow safely."
        path="/blog"
        keywords={[
          "creator marketing blog",
          "how to hire UGC creators",
          "best TikTok creators to work with",
          "creator rate card guide",
          "influencer marketing guide",
          "brand creator collaborations",
          "UGC playbook",
          "influencer marketing playbook",
        ]}
      />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> Blog & Guides
          </div>
          <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tighter leading-[0.95]">
            Playbooks for the{" "}
            <span className="nexus-gradient-text">modern creator economy</span>.
          </h1>
          <p className="text-zinc-400 mt-4 text-lg">
            How to hire, price, brief and get paid — no fluff.
          </p>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-72 rounded-2xl bg-[#0C0C10] hairline animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="blog-empty">
            <div className="font-display text-2xl mb-2">No articles yet</div>
          </div>
        ) : (
          <div className="space-y-10" data-testid="blog-list">
            {featured && (
              <Link
                to={`/blog/${featured.slug}`}
                className="grid lg:grid-cols-2 rounded-3xl overflow-hidden hairline bg-[#0C0C10] card-hover beam-hover"
                data-testid="blog-featured"
              >
                <div className="aspect-[16/9] lg:aspect-auto overflow-hidden">
                  <img src={featured.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-8 lg:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${catCls(featured.category)}`}>
                      {featured.category}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {featured.reading_minutes} min read
                    </span>
                  </div>
                  <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight leading-tight">
                    {featured.title}
                  </h2>
                  <p className="text-zinc-400 mt-4 max-w-xl">{featured.description}</p>
                  <div className="mt-6 flex items-center gap-3 text-sm">
                    <div className="h-8 w-8 rounded-full nexus-gradient flex items-center justify-center text-xs font-bold">
                      N
                    </div>
                    <div>
                      <div className="font-semibold">{featured.author}</div>
                      <div className="text-xs text-zinc-500">{formatDate(featured.published_at)}</div>
                    </div>
                    <span className="ml-auto nexus-gradient-text font-semibold flex items-center gap-1 text-sm">
                      Read <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="rounded-2xl overflow-hidden hairline bg-[#0C0C10] card-hover beam-hover flex flex-col"
                    data-testid={`blog-card-${p.slug}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={p.cover_image} alt="" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${catCls(p.category)}`}>
                          {p.category}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {p.reading_minutes} min
                        </span>
                      </div>
                      <h3 className="font-display font-bold text-xl leading-tight mb-2">{p.title}</h3>
                      <p className="text-sm text-zinc-400 line-clamp-3 flex-1">{p.description}</p>
                      <div className="text-xs text-zinc-500 mt-4">{formatDate(p.published_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
